/**
 * Pending / error UI for the email-verification gate (ENG-3797, spec §3/§4/§7/§8).
 *
 * Presentation only — all gate logic (the verification poll, the verified
 * latch, auto-dismiss, error/retry transitions, flag-gating) lives in
 * gate.tsx/logic.ts and is untouched here. This module renders the two blocking
 * screens the gate can show (pending and terminal error) and owns nothing but
 * their look + the local change-email form state.
 *
 * Every interactive element uses the design-system `Button` / `Anchor` (never
 * raw <button>/<a>) so it is keyboard-focusable and themed — per the
 * ui-architecture rule. Surfaces, spacing, radius, and color all come from
 * design tokens (bg-base-100, text-muted, rounded-12, …) resolved against the
 * active [data-theme], so the card is correct in dark and light and across the
 * Iris / Hive whitelabel. Copy uses `vendorName()` so the brand follows the
 * build rather than being hardcoded.
 */

import { useState, type ReactElement, type ReactNode } from 'react';
import { Anchor, Button } from '@studio/design-system/button';
import { Icon } from '@studio/design-system/icon';
import { Input } from '@studio/design-system/input';
import { Label } from '@studio/design-system/label';
import { Logo } from '@studio/design-system/logo';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import type { VerificationSurface } from './logic.js';
import { openSupport } from './support.js';

/**
 * The shared centered shell every gate screen sits in: full-window dark base,
 * vertically + horizontally centered, with the vendor logo above a single
 * elevated card. Factored out so the pending and error screens are visually
 * identical frames and only their contents differ — the polish the redesign
 * adds lives here once rather than being copy-pasted into both states.
 */
const GateShell = ({
  icon,
  iconTone,
  title,
  children
}: {
  icon: string;
  /** Tints the hero badge: brand blue for pending, red for the error screen. */
  iconTone: 'primary' | 'error';
  title: string;
  children: ReactNode;
}): ReactElement => (
  <div className='flex h-full w-full flex-col items-center justify-center gap-24 bg-base-300 p-24'>
    <Logo size='1x' />

    <div className='flex w-full max-w-[420px] flex-col items-center gap-20 rounded-12 border border-base-50 bg-base-100 p-32 text-center text-foreground shadow-lg'>
      <span
        className={
          iconTone === 'error'
            ? 'size-56 flex items-center justify-center rounded-full bg-error/10 text-error'
            : 'size-56 flex items-center justify-center rounded-full bg-primary/10 text-primary'
        }
      >
        <Icon className='text-2xl' icon={icon} />
      </span>

      <div className='flex flex-col gap-8'>
        <h1 className='text-xl font-demibold text-foreground'>{title}</h1>
        {children}
      </div>
    </div>
  </div>
);

/** Render the resend button with its live client-cooldown countdown (spec §7). */
const ResendButton = ({
  variant,
  cooldownRemainingMs,
  onResend
}: {
  /** Primary on the pending screen (the main CTA); secondary on the error screen. */
  variant: 'primary' | 'secondary';
  cooldownRemainingMs: number;
  onResend: () => void;
}): ReactElement => {
  const seconds = Math.ceil(cooldownRemainingMs / 1000);
  const onCooldown = cooldownRemainingMs > 0;

  return (
    <Button className='w-full' variant={variant} size='lg' disabled={onCooldown} onClick={onResend}>
      {onCooldown ? `Resend in ${seconds}s` : 'Resend verification email'}
    </Button>
  );
};

/**
 * A secondary escape-hatch link. The DS `Anchor` ships no color of its own (it
 * inherits), and on this elevated card the inherited color is near-black +
 * illegible — the exact defect the redesign fixes — so we tone it `text-muted`
 * with a `text-foreground` hover explicitly. Wraps `Anchor` (a real focusable
 * element) so every link stays keyboard-reachable; preserves onClick wiring.
 */
const GateLink = ({
  onClick,
  children
}: {
  onClick: () => void;
  children: ReactNode;
}): ReactElement => (
  <Anchor className='text-muted transition-colors hover:text-foreground' onClick={onClick}>
    {children}
  </Anchor>
);

/** A keyboard-focusable secondary action row for the escape-hatch links. */
const SecondaryLinks = ({ children }: { children: ReactNode }): ReactElement => (
  <div className='flex w-full flex-col items-center gap-10 border-t border-base-50 pt-16 text-sm'>
    {children}
  </div>
);

export interface PendingVerificationProps {
  /** Analytics surface (also drives copy nuance if needed later). */
  surface: VerificationSurface;
  /** Target email to show the user; omitted gracefully if unknown. */
  email: string | undefined;
  /** Remaining resend cooldown in ms (0 → enabled). */
  cooldownRemainingMs: number;
  /** Fire a resend (gate owns cooldown + analytics). */
  onResend: () => void;
  /** Submit a new email; resolves after the registry update (spec §8). */
  onChangeEmail: (email: string) => Promise<void>;
  /** Log the user out (spec §8 escape hatch). */
  onLogout: () => void;
}

/**
 * The blocking "check your email" screen shown when the signal is confirmed
 * `unverified` (spec §4 BLOCK / fail-closed).
 */
export const PendingVerification = ({
  email,
  cooldownRemainingMs,
  onResend,
  onChangeEmail,
  onLogout
}: PendingVerificationProps): ReactElement => {
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [nextEmail, setNextEmail] = useState('');
  const [changeError, setChangeError] = useState<string | undefined>();

  const submitChangeEmail = async (): Promise<void> => {
    setChangeError(undefined);
    try {
      await onChangeEmail(nextEmail.trim());
    } catch {
      // Server is authoritative; surface a generic, actionable message.
      setChangeError('We could not update your email. Please try again.');
    }
  };

  return (
    <GateShell icon='notification' iconTone='primary' title='Verify your email to continue'>
      <p className='text-md leading-150 text-muted'>
        We sent a verification link to{' '}
        {email ? (
          <span className='font-demibold text-foreground'>{email}</span>
        ) : (
          'your email address'
        )}
        . Open it to start using {vendorName()}.
      </p>
      <p className='text-sm text-muted'>This page updates automatically once you verify.</p>

      <div className='mt-4 flex w-full flex-col gap-12'>
        <ResendButton
          variant='primary'
          cooldownRemainingMs={cooldownRemainingMs}
          onResend={onResend}
        />

        {isChangingEmail && (
          <div className='flex w-full flex-col gap-8 text-left'>
            <Label htmlFor='verification-new-email'>New email address</Label>
            <Input
              id='verification-new-email'
              type='email'
              size='lg'
              aria-label='New email address'
              placeholder='you@example.com'
              value={nextEmail}
              variant={changeError ? 'error' : undefined}
              focusOnMount
              onChange={(value) => setNextEmail(value)}
              onCommit={() => {
                if (nextEmail.trim().length > 0) {
                  void submitChangeEmail();
                }
              }}
            />
            <Button
              className='w-full'
              variant='secondary'
              size='lg'
              disabled={nextEmail.trim().length === 0}
              onClick={() => void submitChangeEmail()}
            >
              Update email
            </Button>
            {changeError && <p className='text-sm text-error'>{changeError}</p>}
          </div>
        )}
      </div>

      <SecondaryLinks>
        {!isChangingEmail && (
          <GateLink onClick={() => setIsChangingEmail(true)}>Use a different email</GateLink>
        )}
        <GateLink onClick={onLogout}>Log out</GateLink>
        <GateLink onClick={openSupport}>Contact support</GateLink>
      </SecondaryLinks>
    </GateShell>
  );
};

export interface VerificationErrorProps {
  /** Re-attempt resolving the signal (spec §4 retry). */
  onRetry: () => void;
  /** Resend the verification email. */
  onResend: () => void;
  /** Remaining resend cooldown in ms. */
  cooldownRemainingMs: number;
  /** Log out. */
  onLogout: () => void;
}

/**
 * Terminal "we couldn't confirm your email" state (spec §4). Reached when the
 * resolving window elapses still-unknown, or a poll attempt errored. Never
 * auto-allows — the user gets explicit Retry / Resend / Support actions.
 */
export const VerificationError = ({
  onRetry,
  onResend,
  cooldownRemainingMs,
  onLogout
}: VerificationErrorProps): ReactElement => (
  <GateShell icon='alert-triangle' iconTone='error' title="We couldn't confirm your email">
    <p className='text-md leading-150 text-muted'>
      Check your connection and try again. If you just verified, retrying should let you through.
    </p>

    <div className='mt-4 flex w-full flex-col gap-12'>
      <Button className='w-full' variant='primary' size='lg' onClick={onRetry}>
        Retry
      </Button>
      <ResendButton
        variant='secondary'
        cooldownRemainingMs={cooldownRemainingMs}
        onResend={onResend}
      />
    </div>

    <SecondaryLinks>
      <GateLink onClick={onLogout}>Log out</GateLink>
      <GateLink onClick={openSupport}>Contact support</GateLink>
    </SecondaryLinks>
  </GateShell>
);
